/**
 * ADR-025 B-5 "Question changes" timeline.
 *
 * This deliberately reuses the ADR-023 display-safe audit read surface. It is
 * scoped to audit rows whose entity is the current QUESTION; reply, media, and
 * RFI rows have their own entity ids and belong in the global Audit section.
 * The parent workspace lazy-mounts this component only after expansion.
 */

import React, { useCallback, useEffect, useMemo } from 'react';
import { Box, Button, Flex, FlexItem, Text } from '@bigcommerce/big-design';
import { ActivityTimeline, type ActivityTimelineEntry } from '../common/ActivityTimeline';
import type { AuditLogView } from '@find-my-part/types';
import { useAudit } from '../../hooks/useAudit';
import { formatRelativeTime } from '../../utils/formatters';

export interface QuestionChangesTimelineProps {
  questionId: string;
  token: string;
  testId?: string;
}

function humaniseAction(action: string): string {
  const lower = action.replace(/_/g, ' ').toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/**
 * Maps an ADR-023 display-safe audit row onto the shared ActivityTimeline
 * entry shape (B-6). All copy, humanisation, and date formatting stay HERE —
 * the primitive renders exactly the DOM this component rendered inline before
 * the extraction (thin-delegate direction; suites unchanged).
 */
function toTimelineEntry(log: AuditLogView): ActivityTimelineEntry {
  return {
    id: log.logId,
    title: humaniseAction(log.action),
    dateTime: log.createdAt,
    timestampTitle: new Date(log.createdAt).toLocaleString(),
    timestampDisplay: formatRelativeTime(log.createdAt),
    byline: `By ${log.actor}`,
    details: log.details,
    chipLabel: log.reasonRecorded ? 'Reason recorded' : undefined,
  };
}

export function QuestionChangesTimeline({
  questionId,
  token,
  testId = 'question-changes',
}: QuestionChangesTimelineProps): React.ReactElement {
  const { logs, hasMore, isLoading, isLoadingMore, error, hasLoaded, applyFilters, loadOlder } =
    useAudit(token);

  const loadQuestionChanges = useCallback(
    () => applyFilters({ entityType: 'QUESTION', entityId: questionId }),
    [applyFilters, questionId]
  );

  useEffect(() => {
    void loadQuestionChanges();
  }, [loadQuestionChanges]);

  // Defence in depth: a server regression must never make a different
  // question's row visible in this subject-specific workspace.
  const scopedLogs = useMemo(
    () => logs.filter((log) => log.entity.type === 'QUESTION' && log.entity.id === questionId),
    [logs, questionId]
  );
  const omittedOutOfScopeRows = scopedLogs.length !== logs.length;

  return (
    <div data-testid={testId}>
      <Text color="secondary" marginBottom="small">
        Question-level changes only. Reply, media, and RFI activity is not included here; use the
        global Audit section to search across those entity types.
      </Text>

      {isLoading && (
        <div role="status" aria-live="polite" data-testid={`${testId}-loading`}>
          <Text color="secondary">Loading question changes…</Text>
        </div>
      )}

      {error && (
        <Box marginBottom="small" role="alert" data-testid={`${testId}-error`}>
          <Text color="danger" marginBottom="xxSmall">
            {error}
          </Text>
          {(!hasLoaded || logs.length === 0) && (
            <Button
              variant="secondary"
              onClick={() => void loadQuestionChanges()}
              disabled={isLoading}
            >
              Retry question changes
            </Button>
          )}
        </Box>
      )}

      {omittedOutOfScopeRows && (
        <Box marginBottom="small" role="alert" data-testid={`${testId}-scope-warning`}>
          <Text color="danger" marginBottom="none">
            Some entries could not be displayed because they did not match this question.
          </Text>
        </Box>
      )}

      {hasLoaded && scopedLogs.length > 0 && (
        <ActivityTimeline
          entries={scopedLogs.map(toTimelineEntry)}
          ariaLabel="Question changes"
          testId={testId}
          emptyDetailsFallback="No additional display-safe details."
        />
      )}

      {hasLoaded && logs.length === 0 && !isLoading && !error && (
        <Text color="secondary" data-testid={`${testId}-empty`}>
          No question changes have been recorded. Entries exist only from the audit trail&apos;s
          introduction onward, so older activity may predate the trail.
        </Text>
      )}

      {hasLoaded && hasMore && !omittedOutOfScopeRows && (
        <Flex justifyContent="center" marginTop="small">
          <FlexItem>
            <Button
              variant="secondary"
              onClick={() => void loadOlder()}
              disabled={isLoading || isLoadingMore}
              isLoading={isLoadingMore}
              data-testid={`${testId}-load-older`}
            >
              Load older question changes
            </Button>
          </FlexItem>
        </Flex>
      )}
    </div>
  );
}
